/**
 * CZNA-456 — dialog for creating a form-config template by copying an existing
 * one, and for renaming one.
 *
 * Two modes rather than two components: the fields differ by exactly one
 * (`templateId`, which only a create has, because R4 makes the id immutable
 * afterwards) and the submit/error handling is otherwise identical.
 *
 * Error placement follows plan WP-FE CP-1: the two expected failures land on
 * the field that caused them, not in a generic banner.
 * - 409 → "this id is taken for this type", under the id field.
 * - 400 without a structured body → "the id is not in the allowed shape",
 *   also under the id field (the dialog constrains everything else, so a
 *   malformed request can practically only be the id).
 * - 400 WITH a `FormConfigValidationResult` → the catalog validator rejected
 *   the copied payload. That is not about any field in this dialog, so it goes
 *   to a banner listing the blocking errors.
 * - 404 → the template being copied no longer exists (deleted in another tab).
 */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, MessageBanner, Modal, ModalFooter, Row, TextField, } from '@folio/stripes/components';
import { FORM_CONFIG_TEMPLATE_ID_PATTERN } from '../types/api';
var FormConfigTemplateDialog = function (_a) {
    var _b;
    var open = _a.open, mode = _a.mode, sourceName = _a.sourceName, _c = _a.initialLabelCs, initialLabelCs = _c === void 0 ? '' : _c, _d = _a.initialLabelEn, initialLabelEn = _d === void 0 ? '' : _d, isSubmitting = _a.isSubmitting, errorStatus = _a.errorStatus, errorBody = _a.errorBody, _e = _a.timedOut, timedOut = _e === void 0 ? false : _e, _f = _a.failed, failed = _f === void 0 ? false : _f, _g = _a.readTimedOut, readTimedOut = _g === void 0 ? false : _g, onSubmit = _a.onSubmit, onClose = _a.onClose;
    var intl = useIntl();
    var _h = useState(''), templateId = _h[0], setTemplateId = _h[1];
    var _j = useState(initialLabelCs), labelCs = _j[0], setLabelCs = _j[1];
    var _k = useState(initialLabelEn), labelEn = _k[0], setLabelEn = _k[1];
    /** Client-side id complaint, shown before any round trip is made. */
    var _l = useState(null), localIdError = _l[0], setLocalIdError = _l[1];
    // A controlled Modal keeps its internal state across close/reopen, so on the
    // second open the user would otherwise be looking at the previous attempt's
    // half-typed id (RULE: controlled Modal with internal state must reset on the
    // `open` transition).
    useEffect(function () {
        if (open) {
            setTemplateId('');
            setLabelCs(initialLabelCs);
            setLabelEn(initialLabelEn);
            setLocalIdError(null);
        }
        // initialLabel* are derived from the row being acted on; re-running when
        // they change would fight the user's typing mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    var isCreate = mode === 'create';
    /**
     * Server-side complaints that belong on the id field. A 400 WITH a structured
     * body is a validator rejection of the payload and is rendered as a banner
     * instead — see the header comment.
     */
    var serverIdError = (function () {
        if (errorStatus === 409)
            return 'ui-czna-authorities.settings.forms.templates.error.duplicateId';
        if (errorStatus === 400 && errorBody === null) {
            return 'ui-czna-authorities.settings.forms.templates.error.invalidId';
        }
        return null;
    })();
    var idErrorText = localIdError !== null && localIdError !== void 0 ? localIdError : (serverIdError ? intl.formatMessage({ id: serverIdError }) : undefined);
    /**
     * Wrapped in a tagged element rather than handed over as a bare string: the
     * real Stripes `TextField` renders `error` (a `PropTypes.node`) inside its own
     * feedback div, which carries no testid of ours, so from outside the component
     * there was no way to tell "the complaint landed on the id field" from "it
     * landed in a banner" — the two things this dialog exists to keep apart. The
     * component test used to observe a testid its own `TextField` stub invented,
     * i.e. the stub supplied the expectation; the browser never rendered it
     * (CZNA-456 Kontrola). Now both layers look at the same element.
     */
    var idError = idErrorText !== undefined ? (React.createElement("span", { "data-testid": "template-dialog-id-error" }, idErrorText)) : undefined;
    var blockingErrors = ((_b = errorBody === null || errorBody === void 0 ? void 0 : errorBody.errors) !== null && _b !== void 0 ? _b : [])
        .filter(function (e) { return e.severity === 'BLOCKING'; });
    var handleSubmit = function () {
        var trimmedId = templateId.trim();
        if (isCreate && !FORM_CONFIG_TEMPLATE_ID_PATTERN.test(trimmedId)) {
            setLocalIdError(intl.formatMessage({
                id: 'ui-czna-authorities.settings.forms.templates.error.invalidId',
            }));
            return;
        }
        setLocalIdError(null);
        // Only send the languages the user actually filled in — an empty string
        // would persist a blank label, which renders as an unselectable blank row
        // in the picker.
        var templateLabel = {};
        if (labelCs.trim())
            templateLabel.cs = labelCs.trim();
        if (labelEn.trim())
            templateLabel.en = labelEn.trim();
        onSubmit({
            templateId: isCreate ? trimmedId : undefined,
            templateLabel: templateLabel,
        });
    };
    var canSubmit = !isSubmitting
        && (isCreate ? templateId.trim().length > 0 : true)
        // A rename with no label at all would strip the name rather than change it.
        && (isCreate || labelCs.trim().length > 0 || labelEn.trim().length > 0);
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit, disabled: !canSubmit, "data-testid": "template-dialog-submit" },
            React.createElement(FormattedMessage, { id: isCreate
                    ? 'ui-czna-authorities.settings.forms.templates.dialog.create.submit'
                    : 'ui-czna-authorities.settings.forms.templates.dialog.rename.submit' })),
        React.createElement(Button, { onClick: onClose, disabled: isSubmitting, "data-testid": "template-dialog-cancel" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.dialog.cancel" }))));
    return (React.createElement(Modal, { open: open, onClose: onClose, dismissible: true, label: intl.formatMessage({
            id: isCreate
                ? 'ui-czna-authorities.settings.forms.templates.dialog.create.title'
                : 'ui-czna-authorities.settings.forms.templates.dialog.rename.title',
        }), footer: footer },
        React.createElement("div", { "data-testid": "template-dialog" },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: isCreate
                        ? 'ui-czna-authorities.settings.forms.templates.dialog.create.intro'
                        : 'ui-czna-authorities.settings.forms.templates.dialog.rename.intro', values: { source: React.createElement("strong", null, sourceName) } })),
            errorStatus === 404 && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-gone" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.sourceGone" }))),
            timedOut && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-timeout" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.timeout" }))),
            readTimedOut && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-read-timeout" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.readTimeout" }))),
            failed && errorStatus === null && !timedOut && !readTimedOut && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-network" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.network" }))),
            !timedOut && !readTimedOut && errorStatus !== null && ![400, 404, 409].includes(errorStatus) && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-generic" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.generic", values: { status: errorStatus } }))),
            blockingErrors.length > 0 && (React.createElement(MessageBanner, { type: "error", "data-testid": "template-dialog-error-validator" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.validator" }),
                React.createElement("ul", null, blockingErrors.map(function (e) { return (React.createElement("li", { key: "".concat(e.path, "-").concat(e.code) },
                    React.createElement("code", null, e.path),
                    ': ',
                    e.message)); })))),
            isCreate && (React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(TextField, { id: "template-dialog-id", "data-testid": "template-dialog-id", label: intl.formatMessage({
                            id: 'ui-czna-authorities.settings.forms.templates.dialog.field.id',
                        }), value: templateId, onChange: function (e) {
                            setTemplateId(e.target.value);
                            if (localIdError !== null)
                                setLocalIdError(null);
                        }, error: idError, required: true }),
                    React.createElement("p", { style: { marginTop: '-0.5rem', color: '#666', fontSize: '0.85rem' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.dialog.field.id.help" }))))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 6 },
                    React.createElement(TextField, { id: "template-dialog-label-cs", "data-testid": "template-dialog-label-cs", label: intl.formatMessage({
                            id: 'ui-czna-authorities.settings.forms.templates.dialog.field.labelCs',
                        }), value: labelCs, onChange: function (e) { return setLabelCs(e.target.value); } })),
                React.createElement(Col, { xs: 12, md: 6 },
                    React.createElement(TextField, { id: "template-dialog-label-en", "data-testid": "template-dialog-label-en", label: intl.formatMessage({
                            id: 'ui-czna-authorities.settings.forms.templates.dialog.field.labelEn',
                        }), value: labelEn, onChange: function (e) { return setLabelEn(e.target.value); } }))))));
};
export default FormConfigTemplateDialog;
